import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { generatePath, useHistory } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import {
  DeliveryBonusType,
  MenuItemInfo,
  PromotionAccessType,
  SelfServiceCampaignAction,
  SelfServicePromotionDetailsResponse,
} from '@provider-portal/common/api/openapi_gen/deliveryProviderPortal/deliveryCampaignProvider/apis';
import { useModalDialog } from '@provider-portal/common/components/ModalDialog/useModalDialog';
import { smartPromotionRoutes } from '@provider-portal/promotions-common/providers/apiClient/routes';
import {
  Promotion,
  PromotionCardFC,
  PromotionDuration,
  PromotionFooter,
  PromotionStats,
  PromotionSummary,
} from 'promotions/components';

import {
  useSelectedProvider,
  useSelectedProviderCurrency,
  useSelectedProviderId,
} from 'hooks/useSelectedProvider';
import { $tk } from 'utils/appTranslationKey';

import { useCommonAppServices } from '@provider-portal/common/services/ContextProviders';
import { usePromotionSettingsLoader } from '@provider-portal/promotions-common/hooks/usePromotionSettingsLoader';
import { ConfirmButton } from 'components/ConfirmButton/index';
import { FetcherStateView } from 'components/FetcherStateView/index';
import { usePerformNavigate } from 'root/promotions/hooks/usePerformNavigate';

import { MenuItemsUpdateInfo, PromotionCopyPrefillParams } from '@provider-portal/common/api/types';
import { fetchCode } from '../../components/utils';
import { usePromotionsApiFetcher } from '../../providers/apiClient/PromotionsApiFetchersProvider';
import { routes } from '../../routes';
import { findFinalChoiceByTrait } from '../SelectPromotionWizard/SelectPromotionWizard';
import { PromotionDetailsTitle } from './PromotionDetailsTitle';
import { getPromotionState } from './utils';

interface PromotionStatsData {
  orders?: number;
  customers?: number;
  updateDate: number;
  currency?: string;
  newUsers?: number;
  totalSpend?: number;
  totalRevenue?: number;
}

function findError(messages: Record<string, string>, code: number | null): string | undefined {
  return (code && messages[code]) || undefined;
}

function isFoodCampaign(campaign: SelfServicePromotionDetailsResponse | null): boolean {
  if (campaign) {
    return (
      campaign.campaign_bonus_type === DeliveryBonusType.PERCENTAGE_OF_FOOD_PRICE ||
      campaign.campaign_bonus_type === DeliveryBonusType.PERCENTAGE_OF_FOOD_PRICE_WITH_DISCOUNT_CAP
    );
  }
  return false;
}

export const PromotionDetails = () => {
  const intl = useIntl();
  const routeParams = routes.details.useParams();
  const promotionId = useMemo(() => {
    const id = Number(routeParams.promotionId);
    return Number.isNaN(id) ? null : id;
  }, [routeParams.promotionId]);
  const providerId = useSelectedProviderId();
  const providerInfo = useSelectedProvider();
  const providerCurrency = useSelectedProviderCurrency();
  const { notificationService } = useCommonAppServices();
  const permissionsFetcher = usePromotionsApiFetcher().fetchPromotionsPermissions.useFetcher();
  const promotionSettings = usePromotionSettingsLoader(providerId, permissionsFetcher);
  const promotionFetcher = usePromotionsApiFetcher().details.useFetcher();
  const promotionMetaFetcher = usePromotionsApiFetcher().fetchSelfServiceCampaignMeta.useFetcher();
  const cityExclusionTagsFetcher = usePromotionsApiFetcher().getCityExclusionTags.useFetcher();
  const menuItemsFetcher = usePromotionsApiFetcher().getMenuItemsForProviderActiveMenu.useFetcher();
  const signOutFetcher = usePromotionsApiFetcher().singOut.useFetcher();
  const menuItemsUpdateFetcher = usePromotionsApiFetcher().updateMenuItemsForPromotion.useFetcher();

  const goToSmartPromotions = usePerformNavigate(smartPromotionRoutes.promotions);

  const [campaign, setCampaign] = useState<SelfServicePromotionDetailsResponse | null>(null);
  const [selectedMenuItems, setSelectedMenuItems] = useState<MenuItemInfo[]>([]);
  const [cityExclusionTags, setCityExclusionTags] = useState<string[]>([]);
  const [campaignTag, setCampaignTag] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>();
  const history = useHistory();
  const selfServiceOptionsfetcher =
    usePromotionsApiFetcher().availableSelfServiceOptions.useFetcher();
  const [isCopyButtonEnabled, setIsCopyButtonEnabled] = useState(false);

  const { stateViewStringIds, confirmStrings, errorStrings, errorMessages, copyPromotionStrings } =
    useMemo(
      () => ({
        stateViewStringIds: {
          error: $tk.components.PromotionDetails.errorLoading,
          empty: $tk.components.PromotionDetails.errorLoading,
        },
        confirmStrings: {
          buttonText: intl.formatMessage({
            id: $tk.components.PromotionDetails.discardConfirm.buttonText,
          }),
          backText: intl.formatMessage({
            id: $tk.buttons.back,
          }),
          title: intl.formatMessage({ id: $tk.components.PromotionDetails.discardConfirm.title }),
          textUpcoming: intl.formatMessage({
            id: $tk.components.PromotionDetails.discardConfirm.textUpcoming,
          }),
          textActive: intl.formatMessage({
            id: $tk.components.PromotionDetails.discardConfirm.textActive,
          }),
          confirmText: intl.formatMessage({
            id: $tk.components.PromotionDetails.discardConfirm.confirmText,
          }),
          cancelText: intl.formatMessage({ id: $tk.buttons.cancel }),
        },
        copyPromotionStrings: {
          buttonText: intl.formatMessage({
            id: $tk.components.PromotionDetails.copyPromotion.buttonText,
          }),
          notFoundText: intl.formatMessage({
            id: $tk.components.PromotionDetails.copyPromotion.notFoundText,
          }),
        },
        errorStrings: {
          title: intl.formatMessage({ id: $tk.components.PromotionDetails.signOutError.title }),
          text: intl.formatMessage({ id: $tk.components.PromotionDetails.signOutError.text }),
          buttonText: intl.formatMessage({
            id: $tk.components.PromotionDetails.signOutError.button,
          }),
        },
        errorMessages: {
          5862: intl.formatMessage({ id: $tk.components.PromotionDetails.errorMessage.code5862 }),
        },
      }),
      [intl],
    );

  const { Modal, openModalDialog } = useModalDialog(errorStrings);

  const loadPromotion = useCallback(() => {
    if (promotionId && providerId) {
      promotionFetcher
        .fetch({ provider_id: providerId, promotion_id: promotionId })
        .then((data) => {
          if (data?.campaign) {
            setCampaign(data.campaign);
          } else {
            goToSmartPromotions();
          }
        });
    }
  }, [promotionFetcher, promotionId, providerId, goToSmartPromotions]);

  useEffect(() => {
    promotionMetaFetcher
      .fetch({ self_service_campaign_log_id: promotionId })
      .then((promotionMeta) => {
        if (promotionMeta.result && promotionMeta.result.menu_items_info.length) {
          menuItemsFetcher.fetch({ provider_id: providerId }).then((menuData) => {
            const menuItemMap = menuData.items_data.reduce((acc, menuItem) => {
              if (menuItem.product_id) {
                acc[menuItem.product_id] = menuItem;
              } else if (menuItem.global_stable_context_id) {
                acc[menuItem.global_stable_context_id] = menuItem;
              }
              return acc;
            }, {});

            setSelectedMenuItems(
              promotionMeta.result.menu_items_info.map((item) => {
                if (menuItemMap[item.product_id]) {
                  return {
                    global_stable_context_id: item.global_stable_context_id ?? '',
                    name: menuItemMap[item.product_id].name,
                    product_id: menuItemMap[item.product_id].product_id,
                  };
                }
                if (menuItemMap[item.global_stable_context_id]) {
                  return {
                    global_stable_context_id: item.global_stable_context_id,
                    name: menuItemMap[item.global_stable_context_id].name,
                    product_id: menuItemMap[item.global_stable_context_id].product_id,
                  };
                }
                return item;
              }),
            );
          });
          cityExclusionTagsFetcher.fetch({ city_id: providerInfo.cityId }).then((response) => {
            setCityExclusionTags(response.result);
          });
        }
        if (promotionMeta.result.special_tag) {
          setCampaignTag(promotionMeta.result.special_tag);
        }
      })
      .catch(() => {});
  }, [
    promotionId,
    menuItemsFetcher,
    promotionMetaFetcher,
    providerId,
    cityExclusionTagsFetcher,
    providerInfo.cityId,
  ]);

  const selectedMenuItemNames = useMemo(() => {
    return selectedMenuItems
      .map((item) => {
        const selectedLocale = intl.locale;
        const fallBackLocale = 'en-US' in item.name ? 'en-US' : Object.keys(item.name)[0];
        return item.name[selectedLocale] ? item.name[selectedLocale] : item.name[fallBackLocale];
      })
      .join(', ');
  }, [intl.locale, selectedMenuItems]);

  const selectedProductIds = useMemo(() => {
    return selectedMenuItems.map((item) => item.product_id ?? item.global_stable_context_id);
  }, [selectedMenuItems]);

  useEffect(() => loadPromotion(), [loadPromotion]);

  const onBackClick = useCallback(() => {
    history.goBack();
  }, [history]);

  const onAbortConfirm = useCallback(() => {
    if (campaign?.action === SelfServiceCampaignAction.ASSIGNED && providerId && promotionId) {
      signOutFetcher
        .fetch({
          provider_id: providerId,
          promotion_id: promotionId,
        })
        .then(() => loadPromotion())
        .catch((error) => {
          setErrorMessage(findError(errorMessages, fetchCode(error)));
          openModalDialog();
        });
    }
  }, [
    campaign,
    loadPromotion,
    promotionId,
    providerId,
    signOutFetcher,
    openModalDialog,
    setErrorMessage,
    errorMessages,
  ]);

  const onMenuItemsUpdate = useCallback(
    (newItemsToUpdate: MenuItemsUpdateInfo[]) => {
      const isPassingMinPriceCriteria = (itemsToUpdate: MenuItemsUpdateInfo[]) => {
        if (!campaign.selected_items_config.total_items_min_price) {
          return true;
        }
        const selectedItemsConfig = campaign.selected_items_config;
        let totalPrice = 0;
        itemsToUpdate.forEach((item) => {
          totalPrice += item.price;
        });
        return totalPrice > selectedItemsConfig.total_items_min_price;
      };
      const isPassingMinSelectedItemsCriteria = (itemsToUpdate: MenuItemsUpdateInfo[]) => {
        if (!campaign.selected_items_config.min_items_count) {
          return itemsToUpdate.length >= 1;
        }
        const selectedItemsConfig = campaign.selected_items_config;
        return (
          selectedItemsConfig.min_items_count &&
          itemsToUpdate.length >= selectedItemsConfig.min_items_count
        );
      };

      const selectedItemsConfig = campaign.selected_items_config;
      if (!isPassingMinSelectedItemsCriteria(newItemsToUpdate)) {
        const alertMessage = intl.formatMessage(
          {
            id: $tk.components.PromotionDetails.menuItemsUpdate.belowMinItemsCount,
          },
          {
            minimumItemsCount: selectedItemsConfig.min_items_count ?? 1,
          },
        );
        notificationService.showNotification(alertMessage, 'error');
        return;
      }
      if (!isPassingMinPriceCriteria(newItemsToUpdate)) {
        const alertMessage = intl.formatMessage(
          {
            id: $tk.components.PromotionDetails.menuItemsUpdate.belowMinPriceValue,
          },
          {
            minimumTotalPrice: selectedItemsConfig.total_items_min_price,
            currency: newItemsToUpdate[0].currency,
          },
        );
        notificationService.showNotification(alertMessage, 'error');
        return;
      }
      const request = {
        provider_id: providerId,
        city_id: providerInfo.cityId,
        self_service_campaign_log_id: promotionId,
        menu_items_info: newItemsToUpdate,
        campaign_tag: campaignTag,
      };
      menuItemsUpdateFetcher
        .fetch(request)
        .then((response) => {
          setSelectedMenuItems(response.result.menu_items_info);
          notificationService.showNotification(
            intl.formatMessage({
              id: $tk.components.PromotionDetails.menuItemsUpdate.updateSuccess,
            }),
            'success',
          );
        })
        .catch(() => {
          notificationService.showNotification(
            intl.formatMessage({
              id: $tk.components.PromotionDetails.menuItemsUpdate.updateError,
            }),
            'error',
          );
        });
    },
    [
      campaign,
      campaignTag,
      intl,
      menuItemsUpdateFetcher,
      notificationService,
      promotionId,
      providerId,
      providerInfo.cityId,
    ],
  );

  const cardStrings = useMemo(
    () => ({
      title: <PromotionDetailsTitle promotion={campaign} />,
    }),
    [campaign],
  );

  const onClickCopyPromotionHandler = useCallback(() => {
    const url = generatePath(routes.addPromotion);
    const urlQueryParams: PromotionCopyPrefillParams = {
      preselect_by_trait: campaign.trait_slug,
      from_campaign_log_id: campaign.id,
    };
    history.push(`${url}?copyPrefill=${btoa(JSON.stringify(urlQueryParams))}`);
  }, [campaign, history]);

  const providerPermissions = useSelectedProvider().permissions;

  const shouldShowCopyButton = useMemo(() => {
    if (promotionSettings.promotion_access_type !== PromotionAccessType.ALL) {
      return false;
    }
    if (
      !providerPermissions.selfAssignCampaigns ||
      getPromotionState(campaign) !== 'ended' ||
      !providerPermissions.canCreatePromotions
    ) {
      return false;
    }

    if (isFoodCampaign(campaign)) {
      return providerPermissions.canCreateFoodPromotions;
    }
    return true;
  }, [
    campaign,
    providerPermissions.canCreateFoodPromotions,
    providerPermissions.canCreatePromotions,
    providerPermissions.selfAssignCampaigns,
    promotionSettings,
  ]);

  useEffect(() => {
    // check campaign can be copied or not when shouldShowCopyButton
    if (shouldShowCopyButton && campaign) {
      if (
        campaign.is_best_user_campaign &&
        !campaign.best_user_promotion_permissions.is_allowed_to_create_best_user_promotion
      ) {
        setIsCopyButtonEnabled(false);
        return;
      }
      selfServiceOptionsfetcher
        .fetch({ provider_id: providerId })
        .then(() => {
          if (selfServiceOptionsfetcher.data.options) {
            // search trait in section, check campaign can be copied or not
            const found = findFinalChoiceByTrait(
              selfServiceOptionsfetcher.data.options,
              campaign.trait_slug,
            )[0];
            setIsCopyButtonEnabled(Boolean(found));
          } else {
            setIsCopyButtonEnabled(false);
          }
        })
        .catch(() => {
          openModalDialog();
          setIsCopyButtonEnabled(false);
        });
    }
  }, [shouldShowCopyButton, campaign, selfServiceOptionsfetcher, openModalDialog, providerId]);

  const footer = useMemo(() => {
    const shouldShowCancelButtons = !(
      campaign?.action !== SelfServiceCampaignAction.ASSIGNED ||
      getPromotionState(campaign) === 'ended'
    );

    let confirmButtonStrings;

    if (shouldShowCancelButtons) {
      const isActive = campaign?.start_timestamp * 1000 <= Date.now();
      const { textActive, textUpcoming, ...rest } = confirmStrings;
      confirmButtonStrings = {
        ...rest,
        text: isActive ? textActive : textUpcoming,
      };
    }

    return (
      <PromotionFooter hideTacText={!shouldShowCancelButtons}>
        {shouldShowCopyButton && (
          <Box mr={2}>
            <Tooltip
              title={isCopyButtonEnabled ? '' : copyPromotionStrings.notFoundText}
              placement="bottom"
            >
              <Button
                variant="contained"
                color="secondary"
                onClick={onClickCopyPromotionHandler}
                disabled={!isCopyButtonEnabled}
              >
                {copyPromotionStrings.buttonText}
              </Button>
            </Tooltip>
          </Box>
        )}

        {shouldShowCancelButtons && (
          <ConfirmButton
            strings={confirmButtonStrings}
            onConfirm={onAbortConfirm}
            onBack={onBackClick}
            variant="contained"
            color="danger"
          />
        )}
      </PromotionFooter>
    );
  }, [
    campaign,
    confirmStrings,
    onAbortConfirm,
    onBackClick,
    onClickCopyPromotionHandler,
    shouldShowCopyButton,
    isCopyButtonEnabled,
    copyPromotionStrings,
  ]);

  const { promotion, duration, stats } = useMemo<{
    promotion: Promotion | null;
    duration: PromotionDuration | null;
    stats: PromotionStatsData | null;
  }>(() => {
    if (campaign) {
      return {
        promotion: {
          title: campaign.title.value,
          description: campaign.description.value,
          startDate: 0,
          endDate: 0,
          trait: campaign.trait_slug,
          campaignBonusType: campaign.campaign_bonus_type,
        },
        duration: {
          start: campaign.start_timestamp * 1000,
          end: campaign.end_timestamp * 1000,
        },
        stats: {
          orders: Math.floor(campaign.orders),
          customers: Math.floor(campaign.customers),
          updateDate: campaign.last_update,
          currency: campaign.currency,
          newUsers: Math.floor(campaign.new_users),
          totalSpend: campaign.total_spend,
          totalRevenue: campaign.net_orders_value,
        },
      };
    }
    return { promotion: null, duration: null, stats: null };
  }, [campaign]);

  const isPromotionEnded = campaign?.action === SelfServiceCampaignAction.REMOVED;

  return (
    <>
      <FetcherStateView fetcher={promotionFetcher} stringIds={stateViewStringIds}>
        {promotion && duration && stats && (
          <PromotionCardFC strings={cardStrings} onClose={goToSmartPromotions}>
            <PromotionStats {...stats} providerCurrency={providerCurrency?.code} />
            <PromotionSummary
              promotion={promotion}
              duration={duration}
              footer={footer}
              selectedMenuItemNames={selectedMenuItemNames}
              selectedProductIds={selectedProductIds}
              exclusionTags={cityExclusionTags}
              hideMenuEditor={
                isPromotionEnded || !promotionSettings.can_view_selected_items_menu_editor
              }
              onMenuItemsUpdate={onMenuItemsUpdate}
            />
          </PromotionCardFC>
        )}
      </FetcherStateView>
      <Modal text={errorMessage} />
    </>
  );
};
